import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { FC } from 'react';
import {
  Column,
  Container,
  Row,
  Section,
  Spacer,
  spacerAdapter,
  Stack,
} from 'src/components-v2/Layout';
import Box from 'src/components-v2/Layout/Box/Box';
import Image from 'src/components-v2/Media/Image/Image';
import { default as lightModeTheme } from 'src/components-v2/theme';
import { RichTextSwitch } from 'src/containers';
import { CmsComponents } from 'src/lib/constants';
import {
  getCssFontStyle,
  getResponsiveCssFontStyles,
} from 'src/lib/design-system/typography';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import type {
  AssetEntry,
  AuthorEntry,
  ColorEntry,
  FieldsAndSys,
  GradientEntry,
  QuoteEntry,
  QuoteShape,
  SpacerEntry,
} from 'src/types/contentful';
import { styled } from 'threads5/styles/styled';
import { useTheme } from 'threads5/styles/useTheme';
import DecorativeStat from '../DecorativeStat/DecorativeStat';

/** Design spec mandates that gradient tokens are light mode, regardless of contextual theme. */
function getLightModeBackgroundColor(color?: ColorEntry | GradientEntry) {
  // `fields` are missing on a deleted entry
  if (!color || !color.fields) return undefined;

  if ('gradient' in color.fields) {
    return lightModeTheme.palette[`gradient/${color.fields.gradient}`];
  }

  return lightModeTheme.palette[color.fields.color];
}

const StyledAuthorName = styled('h5')(({ theme }) => {
  return {
    margin: 0,
    color: theme.palette['text/contrast-max'],
    ...getResponsiveCssFontStyles(theme, 'P2 SemiBold'),
  };
});

const StyledAuthorAvatar = styled('div')({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Increase selector specificity to override rich text brand logo image styles (which can only be targeted with `img`)
  'picture > img': {
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    objectFit: 'cover',
  },
});

const AuthorAvatar: FC<
  FieldsAndSys<AssetEntry> & {
    borderColor: QuoteShape['accentColor'];
  }
> = ({ fields, sys, borderColor }) => {
  const inspectorProps = useContentfulInspectorMode({
    assetId: sys.id,
  });

  const imageUrl = fields.file.url;

  return (
    <StyledAuthorAvatar
      sx={{
        flexShrink: 0,
        background: getLightModeBackgroundColor(borderColor),
      }}
      {...inspectorProps({ fieldId: 'file' })}
    >
      <Image
        isLazy
        src={`${imageUrl}?w=96&fm=webp&q=70`}
        sources={{
          xs: `${imageUrl}?w=48&fm=webp&q=70 1x, ${imageUrl}?w=96&fm=webp&q=70 2x`,
        }}
        alt={fields.description || fields.title}
        width={48}
        height={48}
      />
    </StyledAuthorAvatar>
  );
};

const Author: FC<
  FieldsAndSys<AuthorEntry> & {
    alignment: 'left' | 'center';
    borderColor?: QuoteShape['accentColor'];
  }
> = ({ fields: { name, blurb, image }, sys, alignment, borderColor }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys.id,
  });

  return (
    <Stack
      {...inspectorProps({ fieldId: 'name' })}
      gap={{
        xs: 1.5,
        md: alignment === 'left' ? 2 : 1.5,
      }}
      alignItems={{
        xs: alignment === 'left' ? 'flex-start' : 'center',
        md: 'center',
      }}
      flexDirection={{
        xs: 'column',
        md: alignment === 'left' ? 'row' : undefined,
      }}
    >
      {image && <AuthorAvatar {...image} borderColor={borderColor} />}

      <Stack gap={1}>
        <StyledAuthorName {...inspectorProps({ fieldId: 'name' })}>
          {name}
        </StyledAuthorName>
        <RichTextSwitch
          sys={sys}
          content={blurb}
          nodes={{
            p: {
              fieldId: 'blurb',
              sx: {
                ...getCssFontStyle('Eyebrow', 'xs'),
                color: 'web3/component/standalone-quote/author-blurb',
              },
              variant: null,
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

const StyledBackgroundImage = styled(Image)(({ theme }) => {
  return {
    zIndex: 0,
    inset: 0,
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    objectFit: 'contain',
    position: 'absolute',
    [theme.breakpoints.down(447)]: {
      // Counteract `container` padding
      left: '-13px',
      right: '-13px',
      width: 'calc(100% + 26px)',
      // Intentionally clip the left-and-right edges of the image instead of scaling it down
      objectFit: 'cover',
      objectPosition: 'bottom center',
    },
  };
});

const BackgroundImage: FC<AssetEntry> = ({ fields }) => {
  if (fields.appearance !== 'Image') {
    return;
  }
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(fields);

  return (
    <StyledBackgroundImage
      isLazy={fields.isLazy}
      sx={fields.styles}
      width={fields.width}
      height={fields.height}
      src={lgImgSrc}
      alt={fields.description ?? fields.title}
      sources={[
        // Custom sources are used instead of `<CmsMediaSwitch>` to allow us to flip to medium
        // image much earlier than is standard for an image asset, as mobile assets
        // for this component are designed for narrow mobile viewports.
        {
          media: '(max-width: 447px)',
          srcSet: makeImageQueryString({ src: smImgSrc, width: 448 }),
        },
        {
          media: '(max-width: 639px)',
          srcSet: makeImageQueryString({ src: mdImgSrc, width: 640 }),
        },
        {
          media: '(max-width: 1023px)',
          srcSet: makeImageQueryString({ src: mdImgSrc, width: 1024 }),
        },
        {
          media: '(max-width: 1279px)',
          srcSet: makeImageQueryString({ src: lgImgSrc, width: 1280 }),
        },
        {
          media: '(max-width: 1439px)',
          srcSet: makeImageQueryString({ src: lgImgSrc, width: 1440 }),
        },
        {
          media: '(max-width: 1729px)',
          srcSet: makeImageQueryString({ src: lgImgSrc, width: 1730 }),
        },
      ]}
    />
  );
};

const StyledSeparator = styled('hr', {
  shouldForwardProp: (prop) => {
    return !['separatorColor'].includes(prop.toString());
  },
})<{
  separatorColor: QuoteShape['accentColor'];
}>(({ separatorColor, theme }) => {
  return {
    background:
      getLightModeBackgroundColor(separatorColor) ??
      theme.palette['text/contrast-min'],
    border: 'none',
    height: '100%',
    margin: 0,
    position: 'relative',
    // Extend however target for Contentful inspector mode
    '::before': {
      content: '""',
      position: 'absolute',
      inset: -10,
    },
    [theme.breakpoints.up('xs')]: {
      width: '100%',
      height: 1,
    },
    [theme.breakpoints.up('md')]: {
      maxHeight: '25.1rem',
      height: '100%',
      width: 1,
    },
  };
});

const QuoteSpacer: FC<FieldsAndSys<SpacerEntry>> = ({ fields, sys }) => {
  return (
    <Spacer
      {...spacerAdapter({ fields, sys })}
      sx={{
        // Elevate spacer above background image to allow users to interact in Contentful inspector mode
        position: 'relative',
        zIndex: 1,
        ...fields.styles,
      }}
    />
  );
};

const Quote: FC<FieldsAndSys<QuoteEntry>> = ({
  fields: { body, styles, decorativeStat, asset, accentColor, spacer },
  sys,
}) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys.id,
  });
  const theme = useTheme();

  const leftAligned = !!decorativeStat;

  return (
    <Section
      {...inspectorProps({ fieldId: 'internalTitle' })}
      className='cms-standalone-quote'
      sx={styles}
    >
      <Container sx={{ position: 'relative' }}>
        {spacer && (
          // Top desktop spacer
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <QuoteSpacer {...spacer} />
          </Box>
        )}

        <Row>
          <Column xs={24} lg={20} lgOffset={2}>
            <Stack
              textAlign={leftAligned ? 'left' : 'center'}
              direction={{ xs: 'column', md: 'row' }}
              alignItems='stretch'
              gap={{ xs: 0, md: 10 }}
            >
              <Box
                sx={{
                  // On desktop, the background image's bounding box includes the quote and an optional decorative stat.
                  // On mobile, the background image is constrainted to the quote area using `position: relative`.
                  position: { xs: 'relative', md: 'static' },
                }}
              >
                {spacer && (
                  // Top mobile spacer: expands background image area
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <QuoteSpacer {...spacer} />
                  </Box>
                )}
                {asset && <BackgroundImage {...asset} />}

                <Stack
                  gap={{
                    xs: 3,
                    md: 4,
                  }}
                  position='relative'
                  zIndex={1}
                  paddingBottom={{
                    xs: !!decorativeStat ? 3 : 0,
                    md: 0,
                  }}
                  sx={{
                    // Company logo styles
                    img: {
                      height: {
                        xs: '26px',
                        md: '40px',
                      },
                      width: 'auto',
                    },
                  }}
                >
                  <RichTextSwitch
                    sys={sys}
                    content={body}
                    nodes={{
                      h3: {
                        fieldId: 'body',
                        variant: null,
                        sx: {
                          ...getResponsiveCssFontStyles(
                            theme,
                            'Medium Headline',
                          ),
                        },
                      },
                      p: {
                        sx: {
                          ':last-of-type:empty': {
                            display: 'none',
                          },
                        },
                      },
                    }}
                    contentTypeOverrides={{
                      [CmsComponents.AUTHOR]: ({
                        sys,
                        fields,
                      }: FieldsAndSys<AuthorEntry>) => {
                        return (
                          <Author
                            sys={sys}
                            fields={fields}
                            alignment={leftAligned ? 'left' : 'center'}
                            borderColor={accentColor}
                          />
                        );
                      },
                    }}
                  />
                </Stack>

                {spacer && !decorativeStat && (
                  // Bottom mobile spacer: expands background image area when decorative stat is not present
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <QuoteSpacer {...spacer} />
                  </Box>
                )}
              </Box>

              {decorativeStat && (
                <>
                  <Box
                    display='flex'
                    alignItems='center'
                    position='relative'
                    zIndex={1}
                    flexShrink={0}
                  >
                    <StyledSeparator
                      {...inspectorProps({ fieldId: 'accentColor' })}
                      separatorColor={accentColor}
                    />
                  </Box>

                  <Stack
                    justifyContent='center'
                    position='relative'
                    zIndex={1}
                    paddingTop={{
                      xs: 3,
                      md: 0,
                    }}
                  >
                    <DecorativeStat
                      {...decorativeStat}
                      layout='Left'
                      appearance='Medium'
                    />
                  </Stack>

                  {spacer && (
                    // Bottom mobile spacer: adds spacing to the bottom of the decorative stat
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <QuoteSpacer {...spacer} />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Column>
        </Row>

        {spacer && (
          // Bottom desktop spacer
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <QuoteSpacer {...spacer} />
          </Box>
        )}
      </Container>
    </Section>
  );
};

export default Quote;
